import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFinanceApi, adminFinanceKeys } from '@/features/admin-finance/api/admin-finance.api';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { financeListSx, financeRowSx } from '@/features/admin-finance/styles/financeSurfaces';

export function FinanceBankAccountsPanel() {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: adminFinanceKeys.pendingBanks({}),
    queryFn: () => adminFinanceApi.listPendingBankAccounts({ limit: 50 }),
  });

  const verify = useMutation({
    mutationFn: (id: string) => adminFinanceApi.verifyBankAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminFinanceKeys.all }),
  });

  const accounts = data?.data ?? [];

  return (
    <Box sx={financeListSx}>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0, m: 2 }}>
          Failed to load pending bank accounts.
        </Alert>
      )}
      {!isLoading && accounts.length === 0 && (
        <Alert severity="success" sx={{ borderRadius: 0, m: 2 }}>
          No bank accounts awaiting verification.
        </Alert>
      )}

      {accounts.map((a) => (
        <Box key={a.id} sx={financeRowSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography fontWeight={700}>{a.accountHolderName}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                {a.bankName} · {a.accountNumberMasked} · {a.ifscCode}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Guide {a.guideId.slice(-8)}
                {a.isPrimary ? ' · Primary' : ''}
              </Typography>
              <StatusBadge status="pending" label={a.verificationStatus} sx={{ mt: 1, borderRadius: 0 }} />
            </Box>
            <Button
              size="small"
              variant="contained"
              sx={{ borderRadius: 0 }}
              disabled={verify.isPending}
              onClick={() => verify.mutate(a.id)}
            >
              Verify
            </Button>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
